import { Lock } from 'lucide-react';
import { LEVELS, type Rhythm, type Sources, type Dynamics } from '@/lib/weekLevels';

/**
 * Блоки кругов 2–4 разбора недели (11.10.2026). Расчёт — weekLevels.ts, без модели.
 * Тон тот же, что у всего разбора: наблюдение, а не приговор; провал — «на что
 * посмотреть», совет — только из того, что человек уже ест.
 */

const PART_COLOR: Record<string, string> = {
  morning: '#F2C76B', day: '#10E58E', evening: '#3DD8FF', late: '#7A6BD9',
};

export function SectionTitle({ n, badge }: { n: number; badge?: string }) {
  const l = LEVELS[n - 1];
  return (
    <div className="flex items-center gap-2 mb-2.5">
      <span className="text-base leading-none">{l.emoji}</span>
      <span className="font-display font-bold text-sm">{l.title}</span>
      {badge && (
        <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-emerald-glow/15 text-emerald-glow font-medium">{badge}</span>
      )}
    </div>
  );
}

export function RhythmSection({ r, badge }: { r: Rhythm; badge?: string }) {
  const maxProtein = Math.max(1, ...r.protein.map((p) => p.grams));
  return (
    <section>
      <SectionTitle n={2} badge={badge} />
      <div className="grid grid-cols-3 gap-2 mb-3">
        {[
          ['Первый приём', r.firstAt],
          ['Последний', r.lastAt],
          ['Окно питания', `${String(r.windowHours).replace('.', ',')} ч`],
        ].map(([label, value]) => (
          <div key={label} className="rounded-xl bg-bg-elevated/60 px-2 py-2 text-center">
            <div className="font-display font-bold text-base tabular-nums">{value}</div>
            <div className="text-[10px] text-ink-dim mt-0.5">{label}</div>
          </div>
        ))}
      </div>

      <div className="text-[11px] text-ink-dim mb-1.5">Когда уходят калории</div>
      <div className="flex h-3 rounded-full overflow-hidden bg-bg-elevated">
        {r.parts.filter((p) => p.percent > 0).map((p) => (
          <div key={p.id} style={{ width: `${p.percent}%`, background: PART_COLOR[p.id] }} />
        ))}
      </div>
      <div className="grid grid-cols-4 gap-1 mt-1.5">
        {r.parts.map((p) => (
          <div key={p.id} className="text-[10px] text-ink-dim">
            <span className="inline-block w-1.5 h-1.5 rounded-full mr-1 align-middle" style={{ background: PART_COLOR[p.id] }} />
            {p.label}
            <div className="text-xs text-ink font-medium tabular-nums">{p.percent}%</div>
          </div>
        ))}
      </div>

      <div className="text-[11px] text-ink-dim mt-3.5 mb-1.5">Белок по приёмам, в среднем за день</div>
      <div className="space-y-1.5">
        {r.protein.map((p) => (
          <div key={p.type} className="flex items-center gap-2 text-xs">
            <span className="w-16 shrink-0 text-ink-muted">{p.label}</span>
            <div className="flex-1 h-2 rounded-full bg-bg-elevated overflow-hidden">
              <div className="h-full rounded-full bg-emerald-glow" style={{ width: `${(p.grams / maxProtein) * 100}%` }} />
            </div>
            <span className="w-10 text-right tabular-nums text-ink-dim">{p.grams} г</span>
          </div>
        ))}
      </div>

      {r.notes.map((n) => (
        <p key={n} className="text-xs leading-relaxed text-ink-muted mt-2.5">{n}</p>
      ))}
      <p className="text-[10px] text-ink-dim mt-2">
        По {r.days} дням с записями. Время берётся из дневника: приём, внесённый задним числом, стоит на обычном часе своего типа.
      </p>
    </section>
  );
}

export function SourcesSection({ s, badge }: { s: Sources; badge?: string }) {
  return (
    <section>
      <SectionTitle n={3} badge={badge} />
      {s.strong.length > 0 && (
        <>
          <div className="text-[11px] text-ink-dim mb-1.5">Кто закрывает твою норму</div>
          <div className="space-y-2">
            {s.strong.map((r) => (
              <div key={r.key} className="text-xs">
                <span className="font-medium">{r.emoji} {r.label}</span>
                <span className="text-ink-dim"> — {r.top.map((t) => `${t.name} ${t.share}%`).join(' · ')}</span>
              </div>
            ))}
          </div>
        </>
      )}

      {s.weak.length > 0 && (
        <>
          <div className="text-[11px] text-ink-dim mt-3.5 mb-1.5">Чем закрыть провалы — из твоего же рациона</div>
          <div className="space-y-2">
            {s.weak.map((r) => (
              <div key={r.key}>
                <div className="text-xs font-medium mb-1">{r.emoji} {r.label}</div>
                <div className="flex flex-wrap gap-1.5">
                  {r.top.map((name) => (
                    <span key={name} className="px-2 py-1 rounded-lg bg-bg-elevated text-[11px] text-ink-muted">{name}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-ink-muted mt-2 leading-snug">
            Ты это уже ешь и покупаешь — добавь любое ещё раз-другой за неделю, и цифра подрастёт без новых продуктов.
          </p>
        </>
      )}

      <div className="text-[11px] text-ink-dim mt-3.5 mb-1.5">Разных продуктов за неделю: <span className="text-ink font-medium">{s.unique}</span></div>
      {s.frequent.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {s.frequent.map((f) => (
            <span key={f.name} className="px-2 py-1 rounded-lg bg-bg-elevated text-[11px] text-ink-muted">
              {f.name} <span className="text-ink-dim">×{f.times}</span>
            </span>
          ))}
        </div>
      )}
    </section>
  );
}

function Shift({ label, from, to, unit, goodWhen }: { label: string; from: number; to: number; unit: string; goodWhen?: 'up' | 'down' }) {
  const diff = to - from;
  const good = goodWhen && diff !== 0 && (goodWhen === 'up' ? diff > 0 : diff < 0);
  return (
    <div className="rounded-xl bg-bg-elevated/60 px-2.5 py-2">
      <div className="text-[10px] text-ink-dim">{label}</div>
      <div className="text-xs tabular-nums mt-0.5">
        <span className="text-ink-dim">{from}{unit}</span>
        <span className="text-ink-dim"> → </span>
        <span className={`font-medium ${good ? 'text-emerald-glow' : ''}`}>{to}{unit}</span>
      </div>
    </div>
  );
}

export function DynamicsSection({ d, badge }: { d: Dynamics; badge?: string }) {
  return (
    <section>
      <SectionTitle n={4} badge={badge} />
      {!d.comparable ? (
        <p className="text-xs leading-relaxed text-ink-muted">
          Прошлая неделя записана {d.prevDays === 0 ? 'пустой' : `всего на ${d.prevDays} ${d.prevDays === 1 ? 'день' : 'дня'}`} —
          сравнивать пока не с чем. Эта неделя уже записана целиком, так что следующий круг покажет разницу.
        </p>
      ) : (
        <>
          {d.up.length > 0 && (
            <div className="space-y-2 mb-3">
              <div className="text-[11px] text-ink-dim">Выросло против прошлой недели</div>
              {d.up.map((r) => <Move key={r.label} r={r} up />)}
            </div>
          )}
          {d.down.length > 0 && (
            <div className="space-y-2 mb-3">
              <div className="text-[11px] text-ink-dim">Просело — стоит вернуть</div>
              {d.down.map((r) => <Move key={r.label} r={r} />)}
            </div>
          )}
          {d.up.length === 0 && d.down.length === 0 && (
            <p className="text-xs text-ink-muted mb-3">По витаминам и минералам неделя почти как прошлая — рацион устойчивый.</p>
          )}
          <div className="grid grid-cols-2 gap-2">
            {d.protein && <Shift label="Белок в день" from={d.protein.from} to={d.protein.to} unit=" г" goodWhen="up" />}
            {d.rainbow && <Shift label="Цветов радуги" from={d.rainbow.from} to={d.rainbow.to} unit="" goodWhen="up" />}
            {d.calories && <Shift label="Калорий в день" from={d.calories.from} to={d.calories.to} unit="" />}
            {d.late && <Shift label="Еды после 21:00" from={d.late.from} to={d.late.to} unit="%" goodWhen="down" />}
          </div>
          <p className="text-[10px] text-ink-dim mt-2">Проценты — от твоей дневной нормы, в среднем за дни с записями.</p>
        </>
      )}
    </section>
  );
}

function Move({ r, up }: { r: { label: string; emoji: string; from: number; to: number }; up?: boolean }) {
  const scale = Math.max(100, r.from, r.to);
  return (
    <div>
      <div className="flex items-baseline justify-between text-xs">
        <span>{r.emoji} {r.label}</span>
        <span className="tabular-nums text-ink-dim">
          {r.from}% → <span className={up ? 'text-emerald-glow font-medium' : 'text-over font-medium'}>{r.to}%</span>
        </span>
      </div>
      <div className="mt-1 space-y-0.5">
        <div className="h-1.5 rounded-full bg-bg-elevated overflow-hidden">
          <div className="h-full rounded-full bg-ink-dim/50" style={{ width: `${(r.from / scale) * 100}%` }} />
        </div>
        <div className="h-1.5 rounded-full bg-bg-elevated overflow-hidden">
          <div className={`h-full rounded-full ${up ? 'bg-emerald-glow' : 'bg-over'}`} style={{ width: `${(r.to / scale) * 100}%` }} />
        </div>
      </div>
    </div>
  );
}

/** Следующий круг — виден, но закрыт: понятно, ЧТО именно откроется (приём с замком, решение Славы). */
export function LockedLevel({ n }: { n: number }) {
  const l = LEVELS[n - 1];
  return (
    <div className="relative rounded-2xl border border-dashed border-line overflow-hidden">
      <div className="p-4 blur-[3px] opacity-50 select-none pointer-events-none" aria-hidden>
        <div className="h-3 w-32 rounded bg-ink-dim/40 mb-3" />
        <div className="grid grid-cols-3 gap-2 mb-3">
          <div className="h-10 rounded-xl bg-bg-elevated" />
          <div className="h-10 rounded-xl bg-bg-elevated" />
          <div className="h-10 rounded-xl bg-bg-elevated" />
        </div>
        <div className="h-3 rounded-full bg-emerald-glow/40 mb-2" />
        <div className="h-2 w-2/3 rounded-full bg-sky-glow/40 mb-1.5" />
        <div className="h-2 w-1/2 rounded-full bg-sky-glow/40" />
      </div>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-5 bg-bg-card/55">
        <div className="w-9 h-9 rounded-full bg-bg-elevated border border-line flex items-center justify-center mb-2">
          <Lock size={15} className="text-emerald-glow" />
        </div>
        <div className="font-display font-bold text-sm">Круг {n}: {l.emoji} {l.title}</div>
        <p className="text-xs text-ink-muted mt-1 leading-snug">Покажу, {l.promise}.</p>
        <p className="text-[11px] text-emerald-glow mt-2">Ещё одна серия — 5 дней подряд с дневником</p>
      </div>
    </div>
  );
}
